import React, { FC, useEffect, useRef, useState } from 'react';

import { useUiContext } from '../../hooks/contexts/ui.context';
import Portal from '../Portal';
import * as Styles from './styles.popover';

type PositionProps = {
  top: number;
  left: number;
};

type PopoverProps = {
  children: React.ReactNode;
  text: string;
  id: string;
  render?: boolean;
};

const Popover: FC<PopoverProps> = ({ children, text, id, render = true }) => {
  const { isMobile } = useUiContext();

  const [show, setShow] = useState(false);
  const [positions, setPositions] = useState<PositionProps>({
    top : 0,
    left : 0,
  });
  const [currentText, setCurrentText] = useState(text);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  let hideTimeout: NodeJS.Timeout;

  const handleMouseOver = (newText: string) => {
    clearTimeout(hideTimeout);
    if (!show) {
      setCurrentText(newText);
      if (triggerRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const popoverRect = popoverRef.current?.getBoundingClientRect() || { width : 0, height : 0 };

        setPositions({
          top : triggerRect.top + window.scrollY - popoverRect.height - 30,
          left : triggerRect.left + window.scrollX,
        });
      }
    }
    setShow(true);
  };

  const handleMouseOut = () => {
    hideTimeout = setTimeout(() => {
      setShow(false);
      setPositions({ top : 0, left : 0 });
    }, 500);
  };

  useEffect(() => {
    return () => {
      clearTimeout(hideTimeout);
    };
  }, []);

  const { top, left } = positions;

  return (
    <Styles.Popover
      $render={render && !isMobile}
      id={id}
      ref={triggerRef}
      onMouseOver={() => handleMouseOver(text)}
      onMouseOut={handleMouseOut}
      onMouseLeave={handleMouseOut}
      style={{ position : 'relative', display : 'inline-block' }}
    >
      {children}
      <Portal>
        {show && (
          <Styles.PopoverContent
            ref={popoverRef}
            id={`${id}-content`}
            $top={top}
            $left={left}
            $show={show && render && !isMobile}
          >
            <span id={`${id}-text`}>{currentText}</span>
          </Styles.PopoverContent>
        )}
      </Portal>
    </Styles.Popover>
  );
};

export default Popover;
